"use client";

import Link from "next/link";
import Image from "next/image";
import { m } from "framer-motion";
import { cdnUrl } from "@/lib/cdn";
import FadeIn from "../ui/FadeIn";

// Evaluated once per bundle load so the copyright year is stable; paired with
// suppressHydrationWarning below so the build-year baked into the prerendered
// HTML can't mismatch the visit-year the client computes at hydration.
const CURRENT_YEAR = new Date().getFullYear();

const footerLinks = {
  Product: [
    { label: "The Co-Analyst", href: "/products#co-analyst" },
    { label: "Market Intelligence", href: "/products#market-intelligence" },
    { label: "Forensic Risk Assessment", href: "/products#forensic" },
    { label: "Agents", href: "/products#agents" },
    { label: "Coverage & MCP", href: "/products#coverage" },
    { label: "Pricing", href: "/pricing" },
  ],
  Solutions: [
    { label: "Institutional Investing", href: "/institutional-investing" },
    { label: "Corporate Strategy", href: "/corporate-strategy" },
    { label: "Securities Law", href: "/securities-law" },
    { label: "Brokerage & Banking", href: "/brokerage-banking" },
    { label: "Public D&O Insurance", href: "/insurance" },
    { label: "Investor Relations", href: "/investor-relations" },
  ],
  Company: [
    { label: "About", href: "/about" },
    { label: "Technology", href: "/technology" },
    { label: "Research", href: "/research" },
    { label: "In the Media", href: "/in-the-media" },
    { label: "Blog", href: "/blog" },
    { label: "Careers", href: "/careers" },
    { label: "Security", href: "/security" },
  ],
  Legal: [
    { label: "Terms of Service", href: "/terms-of-service" },
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Web Scraping Policy", href: "/web-scraping-policy" },
  ],
};

export default function Footer() {
  return (
    <footer className="relative">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6 sm:gap-8 lg:gap-12">
          {/* Brand */}
          <FadeIn className="col-span-2">
            <Link href="/" className="flex items-center gap-3 mb-5">
              <Image
                src={cdnUrl("/images/logos/hudson-labs-logo.svg")}
                alt="Hudson Labs"
                width={140}
                height={29}
                className="h-7 w-auto"
                unoptimized
              />
            </Link>
            <p className="text-sm text-black/55 max-w-xs leading-relaxed font-light">
              Institutional-grade intelligence for public markets. Find what
              matters.
            </p>
            <div className="flex gap-3 mt-6">
              <a
                href="https://www.linkedin.com/company/hudson-labs"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full border border-black/[0.06] flex items-center justify-center text-xs font-medium text-black/55 hover:border-black/20 hover:text-black hover:bg-black/[0.02] transition-all duration-500"
                aria-label="LinkedIn"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
              <a
                href="https://x.com/HudsonLabs"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full border border-black/[0.06] flex items-center justify-center text-xs font-medium text-black/55 hover:border-black/20 hover:text-black hover:bg-black/[0.02] transition-all duration-500"
                aria-label="X / Twitter"
              >
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
            </div>
          </FadeIn>

          {/* Links */}
          {Object.entries(footerLinks).map(([title, links], idx) => (
            <FadeIn key={title} delay={0.1 * (idx + 1)}>
              <p className="text-xs font-medium mb-5 tracking-wide uppercase text-black/55">
                {title}
              </p>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.label}>
                    <Link
                      href={link.href}
                      className="text-sm text-black/55 hover:text-black transition-colors duration-500 font-light"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </FadeIn>
          ))}
        </div>

        {/* Divider */}
        <m.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, ease: [0.76, 0, 0.24, 1] }}
          className="h-px bg-gradient-to-r from-transparent via-black/[0.06] to-transparent my-12 origin-left"
        />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-black/55" suppressHydrationWarning>
            &copy; {CURRENT_YEAR} Hudson Labs. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link
              href="/terms-of-service"
              className="text-xs text-black/55 hover:text-black transition-colors duration-300"
            >
              Terms of Service
            </Link>
            <Link
              href="/privacy"
              className="text-xs text-black/55 hover:text-black transition-colors duration-300"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
